import styled from 'styled-components';
import SharedDatePicker from '../nutrition/SharedDatePicker';

const trackingTokens = {
  radius: {
    md: '10px',
    lg: '12px',
  },
  color: {
    border: '#E1E8ED',
    borderSoft: '#D6DEE6',
    surface: '#FFFFFF',
    surfaceAlt: '#F8F9FA',
    text: 'var(--bleu-mineral)',
    textMuted: '#666666',
    accent: 'var(--vert-vitalite)',
    danger: 'var(--rouge-alerte)',
    editBorder: 'var(--jaune-energie)',
    editBg: '#FFF8E1',
  },
  shadow: {
    card: 'var(--shadow-md)',
    item: 'var(--shadow-sm)',
  },
  spacing: {
    sm: '8px',
    md: '16px',
    lg: '24px',
  },
};

export const TrackingCard = styled.div`
  background: ${trackingTokens.color.surface};
  border: 1px solid ${(props) => props.$borderColor || trackingTokens.color.border};
  border-radius: 16px;
  padding: 0.9rem;
  box-shadow: ${trackingTokens.shadow.card};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: clip;

  @media (max-width: 768px) {
    padding: 0.78rem;
    border-radius: 10px;
  }
`;

export const TrackingForm = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 0.6rem;
  }
`;

export const TrackingItem = styled.div`
  background: ${trackingTokens.color.surface};
  border: 1px solid ${(props) => props.$borderColor || trackingTokens.color.border};
  border-radius: 14px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.74rem;
    gap: 0.62rem;
    border-radius: 10px;
    box-shadow: ${trackingTokens.shadow.item};
  }
`;

export const TrackingField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  grid-column: ${(props) => (props.$full ? '1 / -1' : 'auto')};

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

export const TrackingLabel = styled.span`
  font-size: 0.83rem;
  font-weight: 700;
  color: ${trackingTokens.color.text};
`;

export const TrackingInput = styled.input`
  width: 100%;
  max-width: 100%;
  display: block;
  min-height: 42px;
  border: 1px solid ${(props) => (
    props.$invalid
      ? trackingTokens.color.danger
      : (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.borderSoft)
  )};
  border-radius: ${trackingTokens.radius.md};
  padding: 0.6rem 0.75rem;
  font-size: 16px;
  color: ${trackingTokens.color.text};
  font-family: 'Roboto', sans-serif;
  background: ${(props) => (props.$editing ? trackingTokens.color.editBg : trackingTokens.color.surfaceAlt)};
  box-sizing: border-box;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: ${(props) => (
      props.$invalid
        ? trackingTokens.color.danger
        : (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.accent)
    )};
    box-shadow: ${(props) =>
      props.$invalid
        ? '0 0 0 3px rgba(229, 57, 53, 0.18)'
        : props.$editing
          ? '0 0 0 3px rgba(255, 193, 7, 0.25)'
          : '0 0 0 3px rgba(76, 175, 80, 0.22)'};
  }

  @media (max-width: 768px) {
    min-height: 48px;
    padding: 0.75rem;
    font-size: 16px;
    touch-action: manipulation;
  }
`;

export const TrackingSelect = styled.select`
  width: 100%;
  max-width: 100%;
  min-height: 42px;
  border: 1px solid ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.borderSoft)};
  border-radius: ${trackingTokens.radius.md};
  padding: 0.6rem 0.75rem;
  font-size: 16px;
  color: ${trackingTokens.color.text};
  font-family: 'Roboto', sans-serif;
  background: ${(props) => (props.$editing ? trackingTokens.color.editBg : trackingTokens.color.surfaceAlt)};
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.accent)};
    box-shadow: ${(props) =>
      props.$editing
        ? '0 0 0 3px rgba(255, 193, 7, 0.25)'
        : '0 0 0 3px rgba(76, 175, 80, 0.22)'};
  }

  @media (max-width: 768px) {
    min-height: 48px;
    font-size: 16px;
  }
`;

export const TrackingTextarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  border: 1px solid ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.borderSoft)};
  border-radius: ${trackingTokens.radius.md};
  padding: 0.65rem 0.75rem;
  font-size: 15px;
  color: ${trackingTokens.color.text};
  font-family: 'Roboto', sans-serif;
  min-height: 78px;
  resize: vertical;
  background: ${(props) => (props.$editing ? trackingTokens.color.editBg : trackingTokens.color.surfaceAlt)};
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.accent)};
    box-shadow: ${(props) =>
      props.$editing
        ? '0 0 0 3px rgba(255, 193, 7, 0.25)'
        : '0 0 0 3px rgba(76, 175, 80, 0.22)'};
  }
`;

export const TrackingDatePicker = styled(SharedDatePicker).attrs((props) => ({
  disabled: props.disabled ?? (props.mode === 'friend'),
}))`
  width: 100%;
  max-width: 100%;

  .date-picker-wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .date-picker-trigger {
    min-height: 42px;
    border: 1px solid ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.borderSoft)};
    box-shadow: none;
    padding: 0.3rem 0.34rem 0.3rem 0.45rem;
    background: ${(props) => (props.$editing ? trackingTokens.color.editBg : trackingTokens.color.surfaceAlt)};
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .date-picker-inline-input {
    color: ${trackingTokens.color.text};
  }

  .date-picker-inline-input::placeholder {
    color: ${trackingTokens.color.textMuted};
  }

  .date-picker-open-btn,
  .date-picker-trigger-icon {
    color: ${trackingTokens.color.textMuted};
  }

  .date-picker-trigger:focus-within {
    border-color: ${(props) => (props.$editing ? trackingTokens.color.editBorder : trackingTokens.color.accent)};
    box-shadow: ${(props) =>
      props.$editing
        ? '0 0 0 3px rgba(255, 193, 7, 0.25)'
        : '0 0 0 3px rgba(76, 175, 80, 0.22)'};
  }

  .date-picker-input {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    .date-picker-trigger {
      min-height: 48px;
      padding: 0.45rem 0.4rem 0.45rem 0.56rem;
    }
  }
`;

export const TrackingButtonPrimary = styled.button`
  border: none;
  background: var(--vert-vitalite);
  color: #ffffff;
  border-radius: 8px;
  min-height: 42px;
  padding: 0.6rem 1.2rem;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  width: fit-content;
  max-width: 100%;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const TrackingButtonSecondary = styled(TrackingButtonPrimary)`
  background: var(--bleu-mineral);
  color: #ffffff;
  border: none;
  box-shadow: var(--shadow-sm);
`;

export const TrackingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TrackingPeriodTabs = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.4rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0.65rem 0.9rem 0.75rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background:
    linear-gradient(45deg, rgba(255, 255, 255, 0.08) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 255, 255, 0.08) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.08) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.08) 75%),
    linear-gradient(130deg, #264d77 0%, var(--bleu-mineral) 58%, #173650 100%);
  background-size: 12px 12px, 12px 12px, 12px 12px, 12px 12px, auto;
  background-position: 0 0, 0 6px, 6px -6px, -6px 0, 0 0;
  box-sizing: border-box;
  box-shadow: 0 8px 20px rgba(26, 46, 64, 0.08);
`;

export const TrackingPeriodTab = styled.button`
  border: 1px solid ${(props) =>
    (props.$active ? 'rgba(176, 255, 192, 0.85)' : 'rgba(255, 255, 255, 0.45)')};
  border-radius: 8px;
  background: ${(props) =>
    (props.$active ? 'rgba(76, 175, 80, 0.28)' : 'rgba(255, 255, 255, 0.16)')};
  color: #ffffff;
  min-height: 34px;
  padding: 0.35rem 0.8rem;
  font-size: 0.78rem;
  font-weight: 600;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  box-shadow: none;
  cursor: pointer;

  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    background: ${(props) =>
      (props.$active ? 'rgba(76, 175, 80, 0.28)' : 'rgba(15, 95, 182, 0.26)')} !important;
    border-color: ${(props) =>
      (props.$active ? 'rgba(176, 255, 192, 0.85)' : 'rgba(194, 221, 255, 0.94)')} !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const TrackingOverviewCard = styled(TrackingCard)`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  background: linear-gradient(135deg, #f8fafb, #ffffff);
  border: 2px solid #e1e8ed;
  box-shadow: 0 4px 12px rgba(26, 46, 64, 0.08);
  overflow: visible;
`;

export const TrackingOverviewTitle = styled.div`
  color: ${trackingTokens.color.text};
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

export const TrackingBarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.45rem;
`;

export const TrackingBarColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, -125%);
    min-width: 180px;
    max-width: min(220px, calc(100vw - 48px));
    border: 1px solid #dbeafe;
    background: #f8fbff;
    border-radius: 10px;
    padding: 0.55rem 0.7rem;
    box-shadow: 0 4px 10px rgba(26, 46, 64, 0.1);
    pointer-events: none;
    z-index: 4;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.18s ease, transform 0.18s ease;
    color: #334155;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1.25;
    text-align: left;
    white-space: pre-line;
  }

  &:hover::after,
  &:focus-within::after {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, -132%);
  }

  &:first-child::after,
  &:nth-child(2)::after {
    left: 0;
    right: auto;
    transform: translate(0, -125%);
  }

  &:last-child::after,
  &:nth-last-child(2)::after {
    left: auto;
    right: 0;
    transform: translate(0, -125%);
  }

  &:first-child:hover::after,
  &:first-child:focus-within::after,
  &:nth-child(2):hover::after,
  &:nth-child(2):focus-within::after {
    transform: translate(0, -132%);
  }

  &:last-child:hover::after,
  &:last-child:focus-within::after,
  &:nth-last-child(2):hover::after,
  &:nth-last-child(2):focus-within::after {
    transform: translate(0, -132%);
  }

  &[data-tooltip='']::after,
  &:not([data-tooltip])::after {
    content: '';
    display: none;
  }
`;

export const TrackingBarTrack = styled.div`
  aspect-ratio: 1/1;
  width: 100%;
  background: #eef2f6;
  border-top-left-radius: 100%;
  border-top-right-radius: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.08);
`;

export const TrackingBarFill = styled.div`
  width: 100%;
  border-radius: 9px;
  background: var(--vert-vitalite);
  height: ${(props) => props.$height || '0%'};
  min-height: ${(props) => (props.$height && props.$height !== '0%' ? '8px' : '0')};
`;

export const TrackingBarLabel = styled.div`
  color: ${trackingTokens.color.textMuted};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

export const TrackingOverviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const TrackingOverviewFooterLabel = styled.div`
  color: ${trackingTokens.color.textMuted};
  font-size: 13px;
  font-weight: 600;
`;

export const TrackingOverviewFooterValue = styled.div`
  color: ${trackingTokens.color.text};
  font-size: 16px;
  font-weight: 900;
  font-family: 'Roboto Mono', monospace;
`;

export const TrackingSessionsCard = styled(TrackingCard)`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const TrackingSessionsTitle = styled.div`
  color: ${trackingTokens.color.text};
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

export const TrackingSessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

export const TrackingSessionCard = styled(TrackingItem)`
  gap: 0.45rem;
  border-radius: 14px;
  background: ${trackingTokens.color.surface};
  padding: 0.7rem;
`;

export const TrackingSessionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

export const TrackingSessionTitle = styled.div`
  color: ${trackingTokens.color.text};
  font-size: 15px;
  font-weight: 800;
`;

export const TrackingSessionSubtitle = styled.div`
  margin-top: 0.06rem;
  color: ${trackingTokens.color.textMuted};
  font-size: 13px;
  font-weight: 600;
`;

export const TrackingSessionMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

export const TrackingSessionMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid ${trackingTokens.color.border};
  background: ${trackingTokens.color.surfaceAlt};
  color: ${trackingTokens.color.text};
  font-size: 13px;
  font-weight: 600;
  padding: 0.18rem 0.6rem;
`;

export const TrackingSessionScore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.02rem;
  min-width: 58px;
`;

export const TrackingSessionScoreValue = styled.div`
  color: ${(props) => props.$color || 'var(--vert-vitalite)'};
  font-size: 18px;
  font-weight: 900;
  line-height: 1;
`;

export const TrackingSessionScoreLabel = styled.div`
  color: ${trackingTokens.color.textMuted};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const TrackingSessionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
